/**
 * Shared regular expressions for detecting partial-import SVG calls in
 * OpenSCAD source code.
 *
 * Two forms are supported:
 *   import("file.svg#id")   — select an element by its `id` attribute
 *   import("file.svg[N]")   — select the Nth (zero-based) top-level geometry
 *                              element (skipping <defs>, <title>, <desc>,
 *                              <metadata>, <style>)
 *
 * Both regexes use the `g` flag so they can be used with `exec()` in a loop
 * or with `String.prototype.replace()` callbacks. Always reset `lastIndex`
 * to 0 before reusing them across separate scans of the same source.
 *
 * They are the single source of truth — the main thread (svgFragmentPrepare)
 * and the worker (openSCAD.ts) both import them from here.
 */

/**
 * Matches `import("something.svg#someId")` anywhere in SCAD source.
 * Capture group 1 = the full path including the fragment, e.g. "file.svg#id1".
 *
 * - `[^"#[]+\.svg`  — the path before the `#`, must end in `.svg` and may
 *                      not contain `"`, `#`, or `[`.
 * - `#[^"]+`        — the fragment id (any non-`"` chars after `#`).
 */
export const SVG_FRAGMENT_RE = /import\s*\(\s*"([^"#[]+\.svg#[^"]+)"\s*\)/g;

/**
 * Matches `import("something.svg[N]")` anywhere in SCAD source.
 * Capture group 1 = base SVG path (e.g. "file.svg"),
 * capture group 2 = numeric index string (e.g. "0").
 *
 * - `[^"[]+\.svg`  — the path before `[`, must end in `.svg` and may not
 *                     contain `"` or `[`.
 * - `\[(\d+)\]`    — a non-negative integer index in square brackets.
 */
export const SVG_INDEX_RE = /import\s*\(\s*"([^"[]+\.svg)\[(\d+)\]"\s*\)/g;

/**
 * Cheap test: does the given source contain at least one SVG partial-import
 * call (either form)?  Used as a fast bail-out before doing expensive parsing.
 */
export function hasSvgFragmentImports(code: string): boolean {
  SVG_FRAGMENT_RE.lastIndex = 0;
  SVG_INDEX_RE.lastIndex = 0;
  const hasFragment = SVG_FRAGMENT_RE.test(code);
  SVG_FRAGMENT_RE.lastIndex = 0;
  if (hasFragment) return true;
  const hasIndex = SVG_INDEX_RE.test(code);
  SVG_INDEX_RE.lastIndex = 0;
  return hasIndex;
}
